import React, { useState, useEffect } from 'react';
import { 
  PhotoIcon, 
  TrashIcon, 
  PencilIcon, 
  ClipboardIcon, 
  CloudArrowUpIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  XMarkIcon,
  CheckIcon,
  TagIcon,
  EyeIcon,
  EyeSlashIcon
} from '@heroicons/react/24/outline';
import FileUploader from '../../components/forms/FileUploader';
import Button from '../../components/common/Button';
import Input from '../../components/common/Input';
import Loader from '../../components/common/Loader';
import { 
  uploadMediaFiles, 
  listMediaAssets, 
  deleteMediaAsset, 
  updateMediaAsset,
  getMediaCategories,
  searchMediaAssets,
  bulkUpdateMediaAssets,
  formatFileSize,
  buildMediaDisplayObject,
  extractErrorMessage,
  ASSET_TYPES,
  MEDIA_CATEGORIES
} from '../../services/mediaService';

function MediaManagementPage() {
  const [mediaAssets, setMediaAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState(false);
  const [searching, setSearching] = useState(false);
  
  // Categories
  const [availableCategories, setAvailableCategories] = useState([]);
  
  // Pagination and filtering
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalAssets, setTotalAssets] = useState(0);
  const [filters, setFilters] = useState({
    category: '',
    asset_type: ''
  });
  
  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearchMode, setIsSearchMode] = useState(false);
  
  // Upload state
  const [showUploadSection, setShowUploadSection] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadCategory, setUploadCategory] = useState('packages');
  
  // Edit state
  const [editingAsset, setEditingAsset] = useState(null);
  const [editForm, setEditForm] = useState({
    // title: '', // Temporarily remove title
    alt_text: '',
    caption: '',
    category: '',
    tags: [],
    is_public: true
  });
  
  // Bulk operations
  const [selectedAssets, setSelectedAssets] = useState([]);
  const [showBulkActions, setShowBulkActions] = useState(false);
  const [bulkUpdateData, setBulkUpdateData] = useState({
    category: '',
    is_public: null,
    tags: []
  });
  
  // Copy feedback
  const [copiedAssetId, setCopiedAssetId] = useState(null);
  
  const categories = [
    { value: '', label: 'All Categories' },
    { value: 'packages', label: 'Packages' },
    { value: 'destinations', label: 'Destinations' },
    { value: 'testimonials', label: 'Testimonials' },
    { value: 'general', label: 'General' }
  ];
  
  const fileTypes = [
    { value: '', label: 'All Types' },
    { value: 'image', label: 'Images' },
    { value: 'video', label: 'Videos' }
  ];

  useEffect(() => {
    initializePage();
  }, []);

  useEffect(() => {
    if (isSearchMode && searchQuery.trim()) {
      handleSearch();
    } else if (!isSearchMode) {
      fetchMediaAssets();
    }
  }, [currentPage, filters, isSearchMode]);

  const initializePage = async () => {
    try {
      await Promise.all([
        fetchMediaCategories(),
        fetchMediaAssets()
      ]);
    } catch (err) {
      console.error('Error initializing page:', err);
      setError('Failed to initialize page');
    }
  };

  const fetchMediaCategories = async () => {
    try {
      const response = await getMediaCategories();
      console.log('📂 Categories response:', response);
      
      // Handle direct response structure or nested data structure
      const categories = response.categories || response.data?.data?.categories || response.data?.categories || [];
      
      if (response && categories.length > 0) {
        const categoryOptions = [
          { value: '', label: 'All Categories' },
          ...categories.map(cat => ({
            value: cat.category,
            label: `${cat.category} (${cat.count})`
          }))
        ];
        setAvailableCategories(categoryOptions);
        console.log('✅ Categories loaded:', categoryOptions.length);
      } else {
        console.log('📂 No categories found, using default options');
        setAvailableCategories([
          { value: '', label: 'All Categories' },
          { value: 'packages', label: 'Packages' },
          { value: 'destinations', label: 'Destinations' },
          { value: 'testimonials', label: 'Testimonials' },
          { value: 'general', label: 'General' }
        ]);
      }
    } catch (err) {
      console.error('Error fetching categories:', err);
      // Use default categories on error
      setAvailableCategories([
        { value: '', label: 'All Categories' },
        { value: 'packages', label: 'Packages' },
        { value: 'destinations', label: 'Destinations' },
        { value: 'testimonials', label: 'Testimonials' },
        { value: 'general', label: 'General' }
      ]);
    }
  };

  const fetchMediaAssets = async () => {
    try {
      setLoading(true);
      setError(''); // Clear any previous errors
      console.log('🔍 Fetching media assets with filters:', filters, 'page:', currentPage);
      
      const response = await listMediaAssets(filters, currentPage, 20);
      console.log('📊 Media assets response:', response);
      
      if (response && (response.media_assets || response.data)) {
        // Handle direct response structure or nested data structure
        const assets = response.media_assets || response.data?.data?.media_assets || response.data?.media_assets || [];
        const pagination = response.pagination || response.data?.data?.pagination || response.data?.pagination || {};
        
        console.log('📋 Extracted assets:', assets.length, 'assets');
        console.log('📄 Pagination info:', pagination);
        console.log('🔍 Sample asset data:', assets[0]); // Log first asset for debugging
        
        // Transform assets using helper function
        const transformedAssets = assets.map((asset, index) => {
          try {
            const transformed = buildMediaDisplayObject(asset);
            if (index === 0) {
              console.log('🔄 Original asset data:', asset);
              console.log('🔄 Transformed asset data:', transformed);
            }
            return transformed;
          } catch (error) {
            console.error(`❌ Error transforming asset ${index}:`, error, asset);
            return null;
          }
        }).filter(Boolean); // Remove null entries
        
        setMediaAssets(transformedAssets);
        setTotalPages(pagination.total_pages || 1);
        setTotalAssets(pagination.total_records || 0);
        setCurrentPage(pagination.page || 1);
        
        console.log('✅ Media assets loaded:', transformedAssets.length);
      } else {
        console.error('❌ Invalid response structure:', response);
        setError('Invalid response from server');
      }
    } catch (err) {
      console.error('❌ Error fetching media assets:', err);
      setError(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      setIsSearchMode(false);
      setCurrentPage(1);
      return;
    }

    try {
      setSearching(true);
      setError(''); // Clear any previous errors
      setIsSearchMode(true);
      console.log('🔍 Searching for:', searchQuery, 'with filters:', filters);
      
      const response = await searchMediaAssets(searchQuery, filters, currentPage, 20);
      console.log('📊 Search response:', response);
      
      if (response && (response.media_assets || response.search_results || response.data)) {
        // Handle direct response structure or nested data structure
        const assets = response.media_assets || response.search_results || response.data?.data?.search_results || response.data?.search_results || [];
        const pagination = response.pagination || response.data?.data?.pagination || response.data?.pagination || {};
        
        console.log('📋 Extracted search results:', assets.length, 'assets');
        console.log('📄 Search pagination info:', pagination);
        console.log('🔍 Sample search result:', assets[0]); // Log first asset for debugging
        
        // Transform assets using helper function
        const transformedAssets = assets.map((asset, index) => {
          try {
            return buildMediaDisplayObject(asset);
          } catch (error) {
            console.error(`❌ Error transforming search result ${index}:`, error, asset);
            return null;
          }
        }).filter(Boolean); // Remove null entries
        
        setMediaAssets(transformedAssets);
        setTotalPages(pagination.total_pages || 1);
        setTotalAssets(pagination.total_records || 0);
        setCurrentPage(pagination.page || 1);
        
        console.log('✅ Search results loaded:', transformedAssets.length);
      } else {
        console.error('❌ Invalid search response structure:', response);
        setError('Invalid response from server');
      }
    } catch (err) {
      console.error('❌ Search error:', err);
      setError(extractErrorMessage(err));
    } finally {
      setSearching(false);
    }
  };

  const clearSearch = () => {
    setSearchQuery('');
    setIsSearchMode(false);
    setCurrentPage(1);
  };

  const handleFilesSelected = (files) => {
    setSelectedFiles(files);
  };

  const handleUploadFiles = async () => {
    if (selectedFiles.length === 0) return;
    
    try {
      setUploading(true);
      console.log('🔄 Uploading files:', selectedFiles.length, 'category:', uploadCategory);
      
      const result = await uploadMediaFiles(selectedFiles, uploadCategory);
      console.log('📤 Upload result:', result);
      
      if (result.success) {
        // Reset upload state
        setSelectedFiles([]);
        setShowUploadSection(false);
        
        // Refresh the media list
        await Promise.all([
          fetchMediaAssets(),
          fetchMediaCategories()
        ]);
        
        // Show success message
        let message = `✅ Uploaded ${result.successfulCount} files successfully`;
        if (result.failedCount > 0) {
          message += ` (${result.failedCount} failed)`;
        }
        console.log(message);
      }
    } catch (err) {
      console.error('❌ Upload error:', err);
      setError(extractErrorMessage(err));
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteAsset = async (assetId) => {
    if (!window.confirm('Are you sure you want to delete this media asset?')) {
      return;
    }
    
    try {
      console.log('🗑️ Deleting asset:', assetId);
      const response = await deleteMediaAsset(assetId);
      console.log('✅ Delete response:', response);
      
      // Refresh data
      await Promise.all([
        fetchMediaAssets(),
        fetchMediaCategories()
      ]);
      
      console.log('✅ Media asset deleted successfully');
    } catch (err) {
      console.error('❌ Delete error:', err);
      setError(extractErrorMessage(err));
    }
  };

  const handleEditAsset = (asset) => {
    setEditingAsset(asset.id);
    setEditForm({
      // title: asset.title || asset.original_filename || '', // Temporarily remove title
      alt_text: asset.alt_text || asset.altText || '',
      caption: asset.caption || '',
      category: asset.category || '',
      tags: Array.isArray(asset.tags) ? asset.tags : [],
      is_public: asset.is_public !== undefined ? asset.is_public : (asset.isPublic !== undefined ? asset.isPublic : true)
    });
  };

  const handleSaveEdit = async () => {
    try {
      console.log('✏️ Saving edit for asset:', editingAsset, 'data:', editForm);
      
      const response = await updateMediaAsset(editingAsset, editForm);
      console.log('✅ Update response:', response);
      
      setEditingAsset(null);
      
      // Refresh the media list
      await fetchMediaAssets();
      
      console.log('✅ Media asset updated successfully');
    } catch (err) {
      console.error('❌ Edit error:', err);
      setError(extractErrorMessage(err));
    }
  };

  const handleBulkUpdate = async () => {
    if (selectedAssets.length === 0) {
      setError('No assets selected for bulk update');
      return;
    }

    try {
      console.log('📦 Bulk updating assets:', selectedAssets, 'data:', bulkUpdateData);
      
      // Filter out empty values
      const updateData = {};
      if (bulkUpdateData.category) updateData.category = bulkUpdateData.category;
      if (bulkUpdateData.is_public !== null) updateData.is_public = bulkUpdateData.is_public;
      if (bulkUpdateData.tags.length > 0) updateData.tags = bulkUpdateData.tags;
      
      if (Object.keys(updateData).length === 0) {
        setError('No update data provided');
        return;
      }
      
      const response = await bulkUpdateMediaAssets(selectedAssets, updateData);
      console.log('✅ Bulk update response:', response);
      
      // Reset bulk state
      setSelectedAssets([]);
      setShowBulkActions(false);
      setBulkUpdateData({ category: '', is_public: null, tags: [] });
      
      // Refresh data
      await Promise.all([
        fetchMediaAssets(),
        fetchMediaCategories()
      ]);
      
      console.log(`✅ Bulk updated ${response.data?.updated_count || 0} assets`);
    } catch (err) {
      console.error('❌ Bulk update error:', err);
      setError(extractErrorMessage(err));
    }
  };

  const handleCopyPath = async (asset) => {
    try {
      const urlToCopy = asset.file_url || asset.url;
      await navigator.clipboard.writeText(urlToCopy);
      setCopiedAssetId(asset.id);
      setTimeout(() => setCopiedAssetId(null), 2000);
      console.log('📋 URL copied to clipboard:', urlToCopy);
    } catch (err) {
      console.error('❌ Failed to copy URL:', err);
      setError('Failed to copy URL to clipboard');
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setCurrentPage(1);
    setIsSearchMode(false); // Clear search mode when filters change
  };

  const clearFilters = () => {
    setFilters({ category: '', asset_type: '' });
    setCurrentPage(1);
    setIsSearchMode(false);
  };

  const handleAssetSelection = (assetId, isSelected) => {
    if (isSelected) {
      setSelectedAssets(prev => [...prev, assetId]);
    } else {
      setSelectedAssets(prev => prev.filter(id => id !== assetId));
    }
  };

  const handleSelectAll = (isSelected) => {
    if (isSelected) {
      setSelectedAssets(mediaAssets.map(asset => asset.id));
    } else {
      setSelectedAssets([]);
    }
  };

  const handleTagAdd = (tag, isEditForm = false) => {
    if (isEditForm) {
      if (!editForm.tags.includes(tag)) {
        setEditForm(prev => ({
          ...prev,
          tags: [...prev.tags, tag]
        }));
      }
    } else {
      if (!bulkUpdateData.tags.includes(tag)) {
        setBulkUpdateData(prev => ({
          ...prev,
          tags: [...prev.tags, tag]
        }));
      }
    }
  };

  const handleTagRemove = (tag, isEditForm = false) => {
    if (isEditForm) {
      setEditForm(prev => ({
        ...prev,
        tags: prev.tags.filter(t => t !== tag)
      }));
    } else {
      setBulkUpdateData(prev => ({
        ...prev,
        tags: prev.tags.filter(t => t !== tag)
      }));
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getAssetTypeIcon = (assetType) => {
    switch (assetType) {
      case ASSET_TYPES.IMAGE:
        return <PhotoIcon className="h-5 w-5" />;
      case ASSET_TYPES.VIDEO:
        return <PhotoIcon className="h-5 w-5" />; // You can add a video icon
      default:
        return <PhotoIcon className="h-5 w-5" />;
    }
  };

  const getAssetTypeColor = (assetType) => {
    switch (assetType) {
      case ASSET_TYPES.IMAGE:
        return 'bg-blue-100 text-blue-800';
      case ASSET_TYPES.VIDEO:
        return 'bg-green-100 text-green-800';
      case ASSET_TYPES.DOCUMENT:
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading && mediaAssets.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 via-blue-50 to-indigo-100">
        <Loader />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-indigo-100 p-2">
      <div className="max-w-full mx-2">
        {/* Header */}
        <div className="bg-white rounded-xl shadow-custom-soft p-4 mb-4 border border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <div className="w-3 h-3 bg-indigo-500 rounded-full mr-3"></div>
              <h1 className="text-2xl font-bold text-gray-900">Media Management</h1>
            </div>
            <div className="flex items-center space-x-3">
              {!isSearchMode && (
                <span className="text-sm text-gray-600">
                  {totalAssets} assets total
                </span>
              )}
              {isSearchMode && (
                <span className="text-sm text-blue-600">
                  {totalAssets} search results for "{searchQuery}"
                </span>
              )}
              <Button
                onClick={() => setShowUploadSection(!showUploadSection)}
                className="bg-gradient-to-r from-indigo-500 to-blue-600 hover:from-indigo-600 hover:to-blue-700 text-white shadow-lg hover:shadow-xl transition-all duration-200"
              >
                <CloudArrowUpIcon className="w-5 h-5 mr-2" />
                Upload Media
              </Button>
            </div>
          </div>
          
          {error && (
            <div className="bg-gradient-to-r from-red-50 to-pink-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">
              <div className="flex items-center">
                <div className="w-2 h-2 bg-red-500 rounded-full mr-3"></div>
                {error}
              </div>
            </div>
          )}
        </div>

        {/* Upload Section */}
        {showUploadSection && (
          <div className="bg-white rounded-xl shadow-custom-soft p-4 mb-4 border border-gray-200">
            <div className="flex items-center mb-4">
              <CloudArrowUpIcon className="w-5 h-5 text-indigo-500 mr-2" />
              <h2 className="text-lg font-semibold text-gray-900">Upload New Media</h2>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="md:col-span-2">
                <FileUploader
                  label="Upload Media Files"
                  multiple={true}
                  initialFiles={selectedFiles}
                  onUploadComplete={handleFilesSelected}
                  onRemove={(fileId) => {
                    setSelectedFiles(prev => prev.filter(file => file.id !== fileId));
                  }}
                  minFiles={1}
                />
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Category
                  </label>
                  <select
                    value={uploadCategory}
                    onChange={(e) => setUploadCategory(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  >
                    {categories.slice(1).map(cat => (
                      <option key={cat.value} value={cat.value}>
                        {cat.label}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div className="flex space-x-3">
                  <Button
                    onClick={handleUploadFiles}
                    disabled={selectedFiles.length === 0 || uploading}
                    className="flex-1 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white disabled:opacity-50"
                  >
                    {uploading ? (
                      <>
                        <Loader className="w-4 h-4 mr-2" />
                        Uploading...
                      </>
                    ) : (
                      <>
                        <CloudArrowUpIcon className="w-4 h-4 mr-2" />
                        Upload
                      </>
                    )}
                  </Button>
                  <Button
                    onClick={() => {
                      setSelectedFiles([]);
                      setShowUploadSection(false);
                    }}
                    variant="outline"
                    className="px-4"
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Search and Filters */}
        <div className="bg-white rounded-xl shadow-custom-soft p-4 mb-4 border border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <MagnifyingGlassIcon className="w-5 h-5 text-gray-500 mr-2" />
              <h3 className="text-lg font-medium text-gray-900">Search & Filters</h3>
            </div>
            <div className="flex space-x-2">
              {isSearchMode && (
                <Button
                  onClick={clearSearch}
                  variant="outline"
                  className="text-sm border-blue-300 text-blue-600 hover:bg-blue-50"
                >
                  <XMarkIcon className="w-4 h-4 mr-1" />
                  Clear Search
                </Button>
              )}
              <Button
                onClick={clearFilters}
                variant="outline"
                className="text-sm"
              >
                <XMarkIcon className="w-4 h-4 mr-1" />
                Clear Filters
              </Button>
              {selectedAssets.length > 0 && (
                <Button
                  onClick={() => setShowBulkActions(!showBulkActions)}
                  className="text-sm bg-indigo-600 hover:bg-indigo-700 text-white"
                >
                  <TagIcon className="w-4 h-4 mr-1" />
                  Bulk Actions ({selectedAssets.length})
                </Button>
              )}
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
            {/* Search */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Search Media
              </label>
              <div className="relative">
                <Input
                  type="text"
                  placeholder="Search by filename, alt text, caption..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      handleSearch();
                    }
                  }}
                  className="pl-10"
                />
                <MagnifyingGlassIcon className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                <div className="absolute right-2 top-2 flex space-x-1">
                  {searchQuery && (
                    <button
                      onClick={clearSearch}
                      className="p-1 hover:bg-gray-100 rounded"
                    >
                      <XMarkIcon className="h-4 w-4 text-gray-400" />
                    </button>
                  )}
                  <Button
                    onClick={handleSearch}
                    disabled={!searchQuery.trim() || searching}
                    size="sm"
                    className="px-3 py-1"
                  >
                    {searching ? (
                      <Loader className="w-3 h-3" />
                    ) : (
                      <MagnifyingGlassIcon className="w-3 h-3" />
                    )}
                  </Button>
                </div>
              </div>
            </div>

            {/* Category Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Category
              </label>
              <select
                value={filters.category}
                onChange={(e) => handleFilterChange('category', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              >
                {availableCategories.map(cat => (
                  <option key={cat.value} value={cat.value}>
                    {cat.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Asset Type Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Asset Type
              </label>
              <select
                value={filters.asset_type}
                onChange={(e) => handleFilterChange('asset_type', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              >
                <option value="">All Types</option>
                <option value="image">Images</option>
                <option value="video">Videos</option>
                <option value="document">Documents</option>
              </select>
            </div>
          </div>

          {/* Bulk Actions */}
          {showBulkActions && selectedAssets.length > 0 && (
            <div className="bg-gradient-to-r from-indigo-50 to-blue-50 border border-indigo-200 rounded-lg p-4 mt-4">
              <h4 className="text-sm font-medium text-indigo-900 mb-3">
                Bulk Update {selectedAssets.length} Selected Assets
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Change Category
                  </label>
                  <select
                    value={bulkUpdateData.category}
                    onChange={(e) => setBulkUpdateData(prev => ({ ...prev, category: e.target.value }))}
                    className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="">Keep Current</option>
                    {availableCategories.filter(cat => cat.value).map(cat => (
                      <option key={cat.value} value={cat.value}>
                        {cat.value}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Visibility
                  </label>
                  <select
                    value={bulkUpdateData.is_public === null ? '' : bulkUpdateData.is_public.toString()}
                    onChange={(e) => setBulkUpdateData(prev => ({ 
                      ...prev, 
                      is_public: e.target.value === '' ? null : e.target.value === 'true' 
                    }))}
                    className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="">Keep Current</option>
                    <option value="true">Public</option>
                    <option value="false">Private</option>
                  </select>
                </div>
                <div className="md:col-span-2 flex items-end space-x-2">
                  <Button
                    onClick={handleBulkUpdate}
                    className="bg-indigo-600 hover:bg-indigo-700 text-white text-sm"
                  >
                    Apply Changes
                  </Button>
                  <Button
                    onClick={() => {
                      setShowBulkActions(false);
                      setSelectedAssets([]);
                      setBulkUpdateData({ category: '', is_public: null, tags: [] });
                    }}
                    variant="outline"
                    className="text-sm"
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Media Assets Table */}
        <div className="bg-white rounded-xl shadow-custom-soft border border-gray-200 overflow-hidden">
          {loading ? (
            <div className="p-8 text-center">
              <Loader />
            </div>
          ) : mediaAssets.length === 0 ? (
            <div className="p-8 text-center">
              <PhotoIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No media assets found</h3>
              <p className="text-gray-500 mb-4">Upload your first media asset to get started</p>
              <Button
                onClick={() => setShowUploadSection(true)}
                className="bg-gradient-to-r from-indigo-500 to-blue-600 hover:from-indigo-600 hover:to-blue-700 text-white"
              >
                <CloudArrowUpIcon className="w-4 h-4 mr-2" />
                Upload Media
              </Button>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gradient-to-r from-gray-50 to-blue-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        <input
                          type="checkbox"
                          checked={selectedAssets.length === mediaAssets.length && mediaAssets.length > 0}
                          onChange={(e) => handleSelectAll(e.target.checked)}
                          className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                        />
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Preview
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Details
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Type & Category
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Size & Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Uploaded
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {mediaAssets.map((asset) => (
                      <tr key={asset.id} className={`hover:bg-gray-50 transition-colors duration-150 ${selectedAssets.includes(asset.id) ? 'bg-blue-50' : ''}`}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <input
                            type="checkbox"
                            checked={selectedAssets.includes(asset.id)}
                            onChange={(e) => handleAssetSelection(asset.id, e.target.checked)}
                            className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                          />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="w-16 h-16 bg-gray-100 rounded-lg overflow-hidden border border-gray-200">
                            {asset.file_type === 'image' ? (
                              <img
                                src={asset.file_url}
                                alt={asset.alt_text || asset.original_filename}
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  e.target.nextSibling.style.display = 'flex';
                                }}
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center bg-gray-100">
                                <PhotoIcon className="w-8 h-8 text-gray-400" />
                              </div>
                            )}
                            <div className="w-full h-full hidden items-center justify-center bg-gray-100">
                              <PhotoIcon className="w-8 h-8 text-gray-400" />
                            </div>
                          </div>
                        </td>
                        
                        <td className="px-6 py-4">
                          {editingAsset === asset.id ? (
                            <div className="space-y-2">
                              <Input
                                type="text"
                                value={editForm.alt_text}
                                onChange={(e) => setEditForm(prev => ({ ...prev, alt_text: e.target.value }))}
                                placeholder="Alt text"
                                className="text-sm"
                              />
                              <Input
                                type="text"
                                value={editForm.caption}
                                onChange={(e) => setEditForm(prev => ({ ...prev, caption: e.target.value }))}
                                placeholder="Caption"
                                className="text-sm"
                              />
                              <select
                                value={editForm.category}
                                onChange={(e) => setEditForm(prev => ({ ...prev, category: e.target.value }))}
                                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                              >
                                <option value="">Select Category</option>
                                {availableCategories.filter(cat => cat.value).map(cat => (
                                  <option key={cat.value} value={cat.value}>
                                    {cat.label}
                                  </option>
                                ))}
                              </select>
                            </div>
                          ) : (
                            <div>
                              <div className="text-sm font-medium text-gray-900">
                                {asset.title || asset.original_filename}
                              </div>
                              <div className="text-sm text-gray-500">
                                {asset.original_filename}
                              </div>
                              {asset.alt_text && (
                                <div className="text-xs text-gray-400 mt-1">
                                  Alt: {asset.alt_text}
                                </div>
                              )}
                              {asset.caption && (
                                <div className="text-xs text-gray-500 mt-1">
                                  Caption: {asset.caption}
                                </div>
                              )}
                              {asset.tags && Array.isArray(asset.tags) && asset.tags.length > 0 && (
                                <div className="flex flex-wrap gap-1 mt-2">
                                  {asset.tags.slice(0, 3).map((tag, index) => (
                                    <span
                                      key={index}
                                      className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800"
                                    >
                                      {tag}
                                    </span>
                                  ))}
                                  {asset.tags.length > 3 && (
                                    <span className="text-gray-500 text-xs">+{asset.tags.length - 3}</span>
                                  )}
                                </div>
                              )}
                            </div>
                          )}
                        </td>
                        
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="space-y-2">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              asset.file_type === 'image' ? 'bg-green-100 text-green-800' :
                              asset.file_type === 'video' ? 'bg-purple-100 text-purple-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {asset.file_type}
                            </span>
                            <div className="text-xs text-gray-500">
                              {asset.category || 'Uncategorized'}
                            </div>
                          </div>
                        </td>
                        
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="space-y-1">
                            <div className="text-sm text-gray-900">
                              {formatFileSize(asset.file_size)}
                            </div>
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                              asset.is_public ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                            }`}>
                              {asset.is_public ? 'Public' : 'Private'}
                            </span>
                          </div>
                        </td>
                        
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          <div className="space-y-1">
                            <div>{formatDate(asset.created_at)}</div>
                            <div className="text-xs text-gray-400">
                              {new Date(asset.created_at).toLocaleTimeString()}
                            </div>
                          </div>
                        </td>
                        
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex items-center space-x-2">
                            {editingAsset === asset.id ? (
                              <>
                                <Button
                                  onClick={handleSaveEdit}
                                  className="bg-green-500 hover:bg-green-600 text-white px-3 py-1 text-xs"
                                >
                                  <CheckIcon className="w-3 h-3" />
                                </Button>
                                <Button
                                  onClick={() => setEditingAsset(null)}
                                  variant="outline"
                                  className="px-3 py-1 text-xs"
                                >
                                  <XMarkIcon className="w-3 h-3" />
                                </Button>
                              </>
                            ) : (
                              <>
                                <Button
                                  onClick={() => window.open(asset.file_url || asset.url, '_blank')}
                                  className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 text-xs"
                                  title="View asset"
                                >
                                  <EyeIcon className="w-3 h-3" />
                                </Button>
                                <Button
                                  onClick={() => handleCopyPath(asset)}
                                  className={`px-3 py-1 text-xs transition-all duration-200 ${
                                    copiedAssetId === asset.id
                                      ? 'bg-green-500 hover:bg-green-600 text-white'
                                      : 'bg-gray-500 hover:bg-gray-600 text-white'
                                  }`}
                                  title="Copy URL"
                                >
                                  {copiedAssetId === asset.id ? (
                                    <CheckIcon className="w-3 h-3" />
                                  ) : (
                                    <ClipboardIcon className="w-3 h-3" />
                                  )}
                                </Button>
                                <Button
                                  onClick={() => handleEditAsset(asset)}
                                  className="bg-indigo-500 hover:bg-indigo-600 text-white px-3 py-1 text-xs"
                                  title="Edit asset"
                                >
                                  <PencilIcon className="w-3 h-3" />
                                </Button>
                                <Button
                                  onClick={() => handleDeleteAsset(asset.id)}
                                  className="bg-red-500 hover:bg-red-600 text-white px-3 py-1 text-xs"
                                  title="Delete asset"
                                >
                                  <TrashIcon className="w-3 h-3" />
                                </Button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              {/* Pagination */}
              {totalPages > 1 && (
                <div className="bg-white px-4 py-3 border-t border-gray-200 sm:px-6">
                  <div className="flex items-center justify-between">
                    <div className="text-sm text-gray-700">
                      Showing page {currentPage} of {totalPages} ({totalAssets} total assets)
                    </div>
                    <div className="flex space-x-2">
                      <Button
                        onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                        disabled={currentPage === 1}
                        variant="outline"
                        className="px-3 py-1 text-sm"
                      >
                        Previous
                      </Button>
                      <Button
                        onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                        disabled={currentPage === totalPages}
                        variant="outline"
                        className="px-3 py-1 text-sm"
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default MediaManagementPage;
